import { isEmpty } from 'underscore';
import { Link } from 'react-router-dom';
import moment from 'moment/moment';
import DeviceTimeline from '../../components/DeviceTimeline';
import React from 'react';

/**
 * Takes an array of report data for timesheet report and adds the timeline, converts values based on metric and restricts cols
 * depending on if timesheet is in journeys or days mode
 * @param timeSheetReport the array of input data
 * @param imperialUnit whether imperial, else metric
 * @param journeyOrDay whether in journeys or days filtering mode
 * @param isFetching is the parent report loading data from the API
 * @param companyFeatures feature flags for the current user, to determine if they have permissions to view the timeline
 * @returns Array
 */
export const formatTimesheetReportData = (
    timeSheetReport: any[], 
    imperialUnit: boolean, 
    journeyOrDay: string, 
    isFetching: boolean, 
    companyFeatures: any[],
) => {
    if (!timeSheetReport || !timeSheetReport.length) {
        return null;
    }

    const SECONDS_IN_A_DAY = 86400;
    const TIMELINE_WIDTH = 1000;
    let listReports = timeSheetReport.map((report) => {
        let tempReport: any = {};
        if (!isEmpty(report)) {
            if (journeyOrDay !== 'days') {
                tempReport.actions = (
                    <Link
                        to={`/journey/${report.id}`}
                        className="btn-actions"
                    >
                        View
                    </Link>
                );
            }
        }
        tempReport = {
            ...tempReport,
            ...report,
        };
        const keys = Object.keys(tempReport);
        const mileageConversionRate = 0.621371;
        const mpgConversionRate = 282.481; // L/100km to UK mpg
        return keys.reduce((acc, key) => {
            acc[key] = tempReport[key];
            let newKey = key;
            switch (key) {
                case 'average_speed':
                case 'max_speed':
                case 'trip_odometer_mileage':
                    acc[key] = parseFloat(acc[key]);
                    if (acc[key] && imperialUnit) acc[key] = (acc[key] * mileageConversionRate).toFixed(2);
                    acc[key] = acc[key].toString();
                    if (imperialUnit) {
                        newKey = `${key}_in_Mi`;
                    } else {
                        newKey = `${key}_in_Km`;
                    }
                    acc[newKey] = acc[key];
                    delete acc[key];
                    break;

                case 'mpg':
                    acc[key] = parseFloat(acc[key]);
                    if (acc[key] && imperialUnit) acc[key] = (mpgConversionRate / acc[key]).toFixed(2);
                    acc[key] = acc[key].toString();
                    if (!imperialUnit) {
                        newKey = 'L_/_100_Km';
                        acc[newKey] = acc[key];
                        delete acc[key];
                    }
                    break;
            }

            return acc;
        }, {});
    });
    
    if (!listReports || !listReports.length) {
        return null;
    }

    if (listReports && listReports.length > 0) {
        listReports = listReports.map((report) => {
            let timeDriven = report.time_driven;
            if (!timeDriven) timeDriven = '-';
            let { shift } = report;
            if (!shift) shift = '-';
            let idleTime = report.idle_time;
            if (!idleTime) idleTime = '-';
            let trueIdleTime = report.true_idle_time;
            if (!trueIdleTime) trueIdleTime = '-';
            let tempReport = {
                ...report,
                shift,
                time_driven: timeDriven,
                idle_time: idleTime,
                true_idle: trueIdleTime,
            };

            let tripOdometerMileage = imperialUnit ? report.trip_odometer_mileage_in_Mi : report.trip_odometer_mileage_in_Km;
            if (!parseFloat(tripOdometerMileage)) tripOdometerMileage = '-';
            let mpg = imperialUnit ? report.mpg : report['L_/_100_Km'];
            if (!parseFloat(mpg)) mpg = '-';
            let averageSpeed = imperialUnit ? report.average_speed_in_Mi : report.average_speed_in_Km;
            if (isNaN(parseInt(averageSpeed))) averageSpeed = '-';
            let maxSpeed = imperialUnit ? report.max_speed_in_Mi : report.max_speed_in_Km;
            if (isNaN(parseInt(maxSpeed))) maxSpeed = '-';
            if (imperialUnit) {
                tempReport.mileage_Mi = tripOdometerMileage;
                tempReport.mpg = mpg;
                tempReport.avg_speed_Mi = parseInt(averageSpeed);
                tempReport.max_speed_Mi = parseInt(maxSpeed);
            } else {
                tempReport.mileage_Km = tripOdometerMileage;
                tempReport['L_/_100_Km'] = mpg;
                tempReport.avg_speed_Km = parseInt(averageSpeed);
                tempReport.max_speed_Km = parseInt(maxSpeed);
            }

            delete tempReport.erroneous;
            // Filter and arrange the columns based on the filter input
            const isFilteredByAssets = tempReport.asset && tempReport.registration;
            if (journeyOrDay === 'days') {
                tempReport.date = moment(tempReport.start)
                    .format('DD-MM-YYYY (ddd)'); // Craft a date to go beside the timeline
                tempReport.timeline = ( // Add the timeline
                    <div>
                        {isFetching ? null : (
                            <DeviceTimeline
                                logicalWidth={SECONDS_IN_A_DAY}
                                scaleX={TIMELINE_WIDTH / SECONDS_IN_A_DAY}
                                deviceDetails={{
                                    assetId: tempReport.asset_id,
                                    date: tempReport.start,
                                }}
                                isInline
                            />
                        )}
                    </div>
                );
                if (isFilteredByAssets) {
                    tempReport = {
                        asset: tempReport.asset,
                        driver: tempReport.driver,
                        division: tempReport.division,
                        registration: tempReport.registration,
                        date: tempReport.date,
                        timeline: tempReport.timeline,
                        shift: tempReport.shift,
                        time_driven: tempReport.time_driven,
                        idle_time: tempReport.idle_time,
                        true_idle: tempReport.true_idle_time,
                        start_area_address: tempReport.start_area_address,
                        end_area_address: tempReport.end_area_address,
                        start_area: tempReport.start_area,
                        end_area: tempReport.end_area,
                        [imperialUnit ? 'mileage_Mi' : 'mileage_Km']: tripOdometerMileage,
                        [imperialUnit ? 'mpg' : 'L_/_100_Km']: imperialUnit ? tempReport.mpg : tempReport['L_/_100_Km'],
                        [imperialUnit ? 'avg_speed_Mi' : 'avg_speed_Km']: imperialUnit ? tempReport.avg_speed_Mi : tempReport.avg_speed_Km,
                        [imperialUnit ? 'max_speed_Mi' : 'max_speed_Km']: imperialUnit ? tempReport.max_speed_Mi : tempReport.max_speed_Km,
                    };
                } else {
                    tempReport = {
                        driver: tempReport.driver,
                        asset: tempReport.asset,
                        division: tempReport.division,
                        date: tempReport.date,
                        timeline: tempReport.timeline,
                        shift: tempReport.shift,
                        time_driven: tempReport.time_driven,
                        idle_time: tempReport.idle_time,
                        true_idle: tempReport.true_idle_time,
                        start_area_address: tempReport.start_area_address,
                        end_area_address: tempReport.end_area_address,
                        start_area: tempReport.start_area,
                        end_area: tempReport.end_area,
                        [imperialUnit ? 'mileage_Mi' : 'mileage_Km']: tripOdometerMileage,
                        [imperialUnit ? 'mpg' : 'L_/_100_Km']: imperialUnit ? tempReport.mpg : tempReport['L_/_100_Km'],
                        [imperialUnit ? 'avg_speed_Mi' : 'avg_speed_Km']: imperialUnit ? tempReport.avg_speed_Mi : tempReport.avg_speed_Km,
                        [imperialUnit ? 'max_speed_Mi' : 'max_speed_Km']: imperialUnit ? tempReport.max_speed_Mi : tempReport.max_speed_Km,
                    };
                }
                const companyFeatureIds = companyFeatures.map((feature) => feature.id);
                if (!companyFeatureIds.includes('timeline-on-timesheet-report')) {
                    delete tempReport.timeline;
                    delete tempReport.date;
                }
            } else {
                if (isFilteredByAssets) {
                    tempReport = {
                        actions: tempReport.actions,
                        asset: tempReport.asset,
                        driver: tempReport.driver,
                        division: tempReport.division,
                        registration: tempReport.registration,
                        start_date: tempReport.start?.split(' ')[0],
                        start_time: tempReport.start?.split(' ')[1],
                        end_date: tempReport.end?.split(' ')[0],
                        end_time: tempReport.end?.split(' ')[1],
                        shift: tempReport.shift,
                        time_driven: tempReport.time_driven,
                        idle_time: tempReport.idle_time,
                        true_idle: tempReport.true_idle_time,
                        start_area_address: tempReport.start_area_address,
                        end_area_address: tempReport.end_area_address,
                        start_area: tempReport.start_area,
                        end_area: tempReport.end_area,
                        [imperialUnit ? 'mileage_Mi' : 'mileage_Km']: tripOdometerMileage,
                        [imperialUnit ? 'mpg' : 'L_/_100_Km']: imperialUnit ? tempReport.mpg : tempReport['L_/_100_Km'],
                        [imperialUnit ? 'avg_speed_Mi' : 'avg_speed_Km']: imperialUnit ? tempReport.avg_speed_Mi : tempReport.avg_speed_Km,
                        [imperialUnit ? 'max_speed_Mi' : 'max_speed_Km']: imperialUnit ? tempReport.max_speed_Mi : tempReport.max_speed_Km,
                        action: tempReport.action,
                    };
                } else {
                    tempReport = {
                        actions: tempReport.actions,
                        driver: tempReport.driver,
                        asset: tempReport.asset,
                        division: tempReport.division,
                        start_date: tempReport.start?.split(' ')[0],
                        start_time: tempReport.start?.split(' ')[1],
                        end_date: tempReport.end?.split(' ')[0],
                        end_time: tempReport.end?.split(' ')[1],
                        shift: tempReport.shift,
                        time_driven: tempReport.time_driven,
                        idle_time: tempReport.idle_time,
                        true_idle: tempReport.true_idle_time,
                        start_area_address: tempReport.start_area_address,
                        end_area_address: tempReport.end_area_address,
                        start_area: tempReport.start_area,
                        end_area: tempReport.end_area,
                        [imperialUnit ? 'mileage_Mi' : 'mileage_Km']: tripOdometerMileage,
                        [imperialUnit ? 'mpg' : 'L_/_100_Km']: imperialUnit ? tempReport.mpg : tempReport['L_/_100_Km'],
                        [imperialUnit ? 'avg_speed_Mi' : 'avg_speed_Km']: imperialUnit ? tempReport.avg_speed_Mi : tempReport.avg_speed_Km,
                        [imperialUnit ? 'max_speed_Mi' : 'max_speed_Km']: imperialUnit ? tempReport.max_speed_Mi : tempReport.max_speed_Km,
                        action: tempReport.action,
                    };
                }
            }

            return tempReport;
        });
    }

    return listReports;
};